import { useEffect, useState, useRef } from 'react';
import { Navigate, useParams } from 'react-router';
import { useApi } from '../lib/api-context';
import { useWorkspace } from '../hooks/useWorkspace';
import { createPage } from '../lib/queries';
import { LAST_PAGE_KEY_PREFIX } from '../lib/constants';

export function WorkspaceRedirect() {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const { workspace, isPending, error } = useWorkspace(api, id);
  const [createdPageId, setCreatedPageId] = useState<string | null>(null);
  const creatingRef = useRef(false);

  // For legacy workspaces with zero pages — create a default, then redirect
  useEffect(() => {
    if (!workspace || workspace.pages.length > 0 || creatingRef.current) return;

    creatingRef.current = true;
    let cancelled = false;
    createPage(api, workspace.id, 'Page 1')
      .then((newPage) => {
        if (!cancelled) setCreatedPageId(newPage.id);
      })
      .catch((err) => {
        creatingRef.current = false;
        console.error('Failed to create default page:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [api, workspace]);

  if (isPending || !workspace) {
    if (error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-destructive">
          <p className="text-sm">{error.message || 'Failed to load workspace'}</p>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const pages = workspace.pages;

  // Zero pages — wait for the effect to create one
  if (pages.length === 0) {
    if (createdPageId) {
      return <Navigate to={`/w/${id}/${createdPageId}`} replace />;
    }
    // Still creating...
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // 1. Check localStorage for last-visited page in this workspace
  const lastPageId = localStorage.getItem(`${LAST_PAGE_KEY_PREFIX}${id}`);
  if (lastPageId && pages.some((p) => p.id === lastPageId)) {
    return <Navigate to={`/w/${id}/${lastPageId}`} replace />;
  }

  // 2. First page by sort order (pages are already sorted — length > 0 guaranteed above)
  return <Navigate to={`/w/${id}/${pages[0]!.id}`} replace />;
}
